/**
 * Skip Go API Service
 *
 * Handles all interactions with Skip Go API for cross-chain swaps.
 * Uses the REST API directly since we're signing via parent wallet bridge,
 * not using Skip's built-in wallet connections.
 *
 * All chain IDs, token addresses, and API endpoints are read from networkConfig
 * so they automatically switch between mainnet and testnet.
 */

import { getSkipGoConfig } from './networkConfig';

// Dynamic getters that always reflect current network
export function getChainIdMap(): Record<string, string> {
  return getSkipGoConfig().chainIdMap;
}

export function getNativeDenomMap(): Record<string, string> {
  return getSkipGoConfig().nativeDenomMap;
}

export function getTokenDenomMap(): Record<string, Record<string, string>> {
  return getSkipGoConfig().tokenDenomMap;
}

function getApiBase(): string {
  return getSkipGoConfig().apiBase;
}

// Legacy exports for backward compatibility (read dynamically)
export const CHAIN_ID_MAP: Record<string, string> = new Proxy({} as Record<string, string>, {
  get(_target, prop: string) {
    return getSkipGoConfig().chainIdMap[prop];
  },
  ownKeys() {
    return Object.keys(getSkipGoConfig().chainIdMap);
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    if (prop in getSkipGoConfig().chainIdMap) {
      return { configurable: true, enumerable: true, value: getSkipGoConfig().chainIdMap[prop] };
    }
    return undefined;
  },
  has(_target, prop: string) {
    return prop in getSkipGoConfig().chainIdMap;
  },
});

export const NATIVE_DENOM_MAP: Record<string, string> = new Proxy({} as Record<string, string>, {
  get(_target, prop: string) {
    return getSkipGoConfig().nativeDenomMap[prop];
  },
  ownKeys() {
    return Object.keys(getSkipGoConfig().nativeDenomMap);
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    if (prop in getSkipGoConfig().nativeDenomMap) {
      return { configurable: true, enumerable: true, value: getSkipGoConfig().nativeDenomMap[prop] };
    }
    return undefined;
  },
});

export const TOKEN_DENOM_MAP: Record<string, Record<string, string>> = new Proxy({} as Record<string, Record<string, string>>, {
  get(_target, prop: string) {
    return getSkipGoConfig().tokenDenomMap[prop];
  },
  ownKeys() {
    return Object.keys(getSkipGoConfig().tokenDenomMap);
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    if (prop in getSkipGoConfig().tokenDenomMap) {
      return { configurable: true, enumerable: true, value: getSkipGoConfig().tokenDenomMap[prop] };
    }
    return undefined;
  },
});

// Reverse mapping for display (dynamic)
export function getChainNameMap(): Record<string, string> {
  const chainIdMap = getSkipGoConfig().chainIdMap;
  return Object.entries(chainIdMap).reduce(
    (acc, [name, id]) => ({ ...acc, [id]: name }),
    {} as Record<string, string>
  );
}

export const CHAIN_NAME_MAP: Record<string, string> = new Proxy({} as Record<string, string>, {
  get(_target, prop: string) {
    return getChainNameMap()[prop];
  },
  ownKeys() {
    return Object.keys(getChainNameMap());
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    const map = getChainNameMap();
    if (prop in map) {
      return { configurable: true, enumerable: true, value: map[prop] };
    }
    return undefined;
  },
});

/**
 * Get the Skip Go denom for an asset (reads from current network config)
 */
export function getSkipGoDenom(symbol: string, chainId: string, contractAddress?: string): string {
  // If contract address provided, use it
  if (contractAddress) {
    return contractAddress;
  }

  const config = getSkipGoConfig();

  // Check token-specific mapping
  if (config.tokenDenomMap[symbol]?.[chainId]) {
    return config.tokenDenomMap[symbol][chainId];
  }

  // For native tokens (ETH, MATIC, etc.), use native denom
  if (symbol === 'ETH' || symbol === 'MATIC' || symbol === 'AVAX' || symbol === 'BNB' || symbol === 'SOL') {
    return config.nativeDenomMap[chainId] || `${symbol.toLowerCase()}-native`;
  }

  // Default: return symbol lowercase
  return symbol.toLowerCase();
}

export interface SkipGoRouteRequest {
  sourceAssetDenom: string;
  sourceAssetChainId: string;
  destAssetDenom: string;
  destAssetChainId: string;
  amountIn: string;
  amountOut?: string;
  cumulativeAffiliateFeeBps?: string;
  allowMultiTx?: boolean;
  allowUnsafe?: boolean;
  smartRelay?: boolean;
  goFast?: boolean;
  experimentalFeatures?: string[];
  smartSwapOptions?: {
    splitRoutes?: boolean;
    evmSwaps?: boolean;
  };
}

export interface SkipGoOperation {
  swap?: {
    swapIn: { swapVenue: { name: string; chainId: string } };
    swapOut: unknown;
    estimatedAffiliateFee?: string;
  };
  transfer?: {
    port: string;
    channel: string;
    chainId: string;
    pfmEnabled: boolean;
    destDenom: string;
    bridgeId?: string;
  };
  axelarTransfer?: unknown;
  cctp?: unknown;
  hyperlane?: unknown;
}

export interface SkipGoRoute {
  sourceAssetDenom: string;
  sourceAssetChainId: string;
  destAssetDenom: string;
  destAssetChainId: string;
  amountIn: string;
  amountOut: string;
  operations: SkipGoOperation[];
  requiredChainAddresses: string[];
  chainIds: string[];
  doesSwap: boolean;
  estimatedAmountOut: string;
  swapVenues?: Array<{ name: string; chainId: string }>;
  estimatedFees?: Array<{
    feeType: string;
    bridgeId: string;
    amount: string;
    usdAmount: string;
    originAsset: { denom: string; chainId: string };
    chainId: string;
    txIndex: number;
    operationIndex?: number;
  }>;
  estimatedRouteDurationSeconds?: number;
  txsRequired: number;
  usdAmountIn?: string;
  usdAmountOut?: string;
  swapPriceImpactPercent?: string;
  warning?: {
    type: string;
    message: string;
  };
}

export interface SkipGoMsgsRequest {
  sourceAssetDenom: string;
  sourceAssetChainId: string;
  destAssetDenom: string;
  destAssetChainId: string;
  amountIn: string;
  amountOut: string;
  addressList: string[];
  operations: SkipGoOperation[];
  slippageTolerancePercent?: string;
  timeoutSeconds?: string;
  chainIdsToAffiliates?: Record<string, { affiliates: Array<{ address: string; basisPointsFee: string }> }>;
  postRouteHandler?: {
    wasmMsg?: { contractAddress: string; msg: string };
    autoPilotMsg?: { receiver: string; action: string };
  };
  enableGasWarnings?: boolean;
}

export interface SkipGoCosmosMsg {
  msgTypeUrl: string;
  msg: string; // JSON encoded message
}

export interface SkipGoEvmTx {
  chainId: string;
  to: string;
  value: string;
  data: string;
  requiredErc20Approvals?: Array<{
    tokenContract: string;
    spender: string;
    amount: string;
  }>;
  signersRequired?: string[];
}

export interface SkipGoSvmTx {
  chainId: string;
  tx: string; // Base64 encoded transaction
  signers: string[];
}

export interface SkipGoTx {
  cosmosTx?: {
    chainId: string;
    path: string[];
    msgs: SkipGoCosmosMsg[];
    signerAddress: string;
  };
  evmTx?: SkipGoEvmTx;
  svmTx?: SkipGoSvmTx;
}

export interface SkipGoMsgsResponse {
  msgs: Array<{
    multiChainMsg?: unknown;
    evmTx?: SkipGoEvmTx;
    svmTx?: SkipGoSvmTx;
  }>;
  txs: SkipGoTx[];
  estimatedFees?: Array<{
    feeType: string;
    bridgeId: string;
    amount: string;
    usdAmount: string;
    originAsset: { denom: string; chainId: string };
    chainId: string;
    txIndex: number;
    operationIndex?: number;
  }>;
}

export interface SkipGoSubmitRequest {
  tx: string; // Signed transaction (base64 for Cosmos/SVM, hex for EVM)
  chainId: string;
}

export interface SkipGoSubmitResponse {
  txHash: string;
  success: boolean;
}

export interface SkipGoStatusResponse {
  transfers: Array<{
    state: 'STATE_SUBMITTED' | 'STATE_PENDING' | 'STATE_COMPLETED_SUCCESS' | 'STATE_COMPLETED_ERROR' | 'STATE_ABANDONED' | 'STATE_PENDING_ERROR';
    transferSequence: Array<{
      ibcTransfer?: {
        srcChainId: string;
        dstChainId: string;
        state: string;
        packetTxs: {
          sendTx?: { chainId: string; txHash: string };
          receiveTx?: { chainId: string; txHash: string };
          acknowledgeTx?: { chainId: string; txHash: string };
          timeoutTx?: { chainId: string; txHash: string };
        };
      };
      axelarTransfer?: unknown;
      cctpTransfer?: unknown;
      hyperlaneTransfer?: unknown;
      evmSwap?: {
        chainId: string;
        txHash: string;
        state: string;
      };
    }>;
    nextBlockingTransfer?: unknown;
    transferAssetRelease?: {
      chainId: string;
      denom: string;
      released: boolean;
    };
    error?: {
      code: number;
      message: string;
    };
  }>;
  state: 'STATE_SUBMITTED' | 'STATE_PENDING' | 'STATE_COMPLETED_SUCCESS' | 'STATE_COMPLETED_ERROR' | 'STATE_ABANDONED' | 'STATE_PENDING_ERROR';
  transferSequence: unknown[];
  nextBlockingTransfer?: unknown;
  transferAssetRelease?: unknown;
  error?: {
    code: number;
    message: string;
  };
  status: 'STATE_SUBMITTED' | 'STATE_PENDING' | 'STATE_COMPLETED_SUCCESS' | 'STATE_COMPLETED_ERROR';
}

export interface SkipGoAsset {
  denom: string;
  chainId: string;
  originDenom: string;
  originChainId: string;
  trace: string;
  isCw20: boolean;
  isEvm: boolean;
  isSvm: boolean;
  symbol: string;
  name: string;
  logoUri: string;
  decimals: number;
  tokenContract?: string;
  description?: string;
  coingeckoId?: string;
  recommendedSymbol?: string;
}

export interface SkipGoChain {
  chainName: string;
  chainId: string;
  pfmEnabled: boolean;
  cosmosModuleSupport: {
    authz: boolean;
    feegrant: boolean;
  };
  supportsMemosAndIbcCallbacks: boolean;
  logoUri: string;
  bech32Prefix: string;
  feeAssets: Array<{
    denom: string;
    gasPrice?: {
      low: string;
      average: string;
      high: string;
    };
  }>;
  chainType: 'cosmos' | 'evm' | 'svm';
  ibcCapabilities: {
    cosmosPfm: boolean;
    cosmosIbcHooks: boolean;
    cosmosMemo: boolean;
    cosmosAutopilot: boolean;
  };
  isTestnet: boolean;
}

class SkipGoApiService {
  private apiKey?: string;

  constructor(apiKey?: string) {
    this.apiKey = apiKey;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (this.apiKey) {
      headers['Authorization'] = this.apiKey;
    }
    return headers;
  }

  /**
   * Get supported chains from Skip Go
   */
  async getChains(options?: { includeEvm?: boolean; includeSvm?: boolean; onlyTestnets?: boolean }): Promise<SkipGoChain[]> {
    const params = new URLSearchParams();
    if (options?.includeEvm) params.append('include_evm', 'true');
    if (options?.includeSvm) params.append('include_svm', 'true');
    if (options?.onlyTestnets) params.append('only_testnets', 'true');

    const response = await fetch(`${getApiBase()}/v2/info/chains?${params}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get chains: ${response.statusText}`);
    }

    const data = await response.json();
    return data.chains || [];
  }

  /**
   * Get assets for specific chains
   */
  async getAssets(chainIds?: string[]): Promise<Record<string, SkipGoAsset[]>> {
    const params = new URLSearchParams();
    if (chainIds) {
      chainIds.forEach(id => params.append('chain_ids', id));
    }

    const response = await fetch(`${getApiBase()}/v2/fungible/assets?${params}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get assets: ${response.statusText}`);
    }

    const data = await response.json();
    return data.chain_to_assets_map || {};
  }

  /**
   * Get a swap/transfer route
   */
  async getRoute(request: SkipGoRouteRequest): Promise<SkipGoRoute> {
    const body: Record<string, unknown> = {
      source_asset_denom: request.sourceAssetDenom,
      source_asset_chain_id: request.sourceAssetChainId,
      dest_asset_denom: request.destAssetDenom,
      dest_asset_chain_id: request.destAssetChainId,
      amount_in: request.amountIn,
      cumulative_affiliate_fee_bps: request.cumulativeAffiliateFeeBps || '0',
      allow_multi_tx: request.allowMultiTx ?? true,
      allow_unsafe: request.allowUnsafe ?? true,
      smart_relay: request.smartRelay ?? true,
      go_fast: request.goFast ?? true,
      experimental_features: request.experimentalFeatures || [
        'hyperlane',
        'stargate',
        'eureka',
        'layer_zero',
      ],
      smart_swap_options: request.smartSwapOptions || {
        split_routes: true,
        evm_swaps: true,
      },
    };

    const response = await fetch(`${getApiBase()}/v2/fungible/route`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get route: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      sourceAssetDenom: data.source_asset_denom,
      sourceAssetChainId: data.source_asset_chain_id,
      destAssetDenom: data.dest_asset_denom,
      destAssetChainId: data.dest_asset_chain_id,
      amountIn: data.amount_in,
      amountOut: data.amount_out,
      operations: data.operations,
      requiredChainAddresses: data.required_chain_addresses,
      chainIds: data.chain_ids,
      doesSwap: data.does_swap,
      estimatedAmountOut: data.estimated_amount_out,
      swapVenues: data.swap_venues,
      estimatedFees: data.estimated_fees,
      estimatedRouteDurationSeconds: data.estimated_route_duration_seconds,
      txsRequired: data.txs_required,
      usdAmountIn: data.usd_amount_in,
      usdAmountOut: data.usd_amount_out,
      swapPriceImpactPercent: data.swap_price_impact_percent,
      warning: data.warning,
    };
  }

  /**
   * Get transaction messages for a route
   */
  async getMessages(request: SkipGoMsgsRequest): Promise<SkipGoMsgsResponse> {
    const body = {
      source_asset_denom: request.sourceAssetDenom,
      source_asset_chain_id: request.sourceAssetChainId,
      dest_asset_denom: request.destAssetDenom,
      dest_asset_chain_id: request.destAssetChainId,
      amount_in: request.amountIn,
      amount_out: request.amountOut,
      address_list: request.addressList,
      operations: request.operations,
      slippage_tolerance_percent: request.slippageTolerancePercent || '1',
      timeout_seconds: request.timeoutSeconds || '300',
      enable_gas_warnings: request.enableGasWarnings ?? true,
    };

    const response = await fetch(`${getApiBase()}/v2/fungible/msgs`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get messages: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Combined route + messages in one call
   */
  async getRouteAndMessages(
    routeRequest: SkipGoRouteRequest,
    addressList: string[],
    slippageTolerancePercent?: string
  ): Promise<{ route: SkipGoRoute; messages: SkipGoMsgsResponse }> {
    const body = {
      source_asset_denom: routeRequest.sourceAssetDenom,
      source_asset_chain_id: routeRequest.sourceAssetChainId,
      dest_asset_denom: routeRequest.destAssetDenom,
      dest_asset_chain_id: routeRequest.destAssetChainId,
      amount_in: routeRequest.amountIn,
      address_list: addressList,
      slippage_tolerance_percent: slippageTolerancePercent || '1',
      allow_multi_tx: routeRequest.allowMultiTx ?? true,
      allow_unsafe: routeRequest.allowUnsafe ?? false,
      smart_relay: routeRequest.smartRelay ?? true,
    };

    const response = await fetch(`${getApiBase()}/v2/fungible/msgs_direct`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get route and messages: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      route: {
        sourceAssetDenom: data.route?.source_asset_denom || routeRequest.sourceAssetDenom,
        sourceAssetChainId: data.route?.source_asset_chain_id || routeRequest.sourceAssetChainId,
        destAssetDenom: data.route?.dest_asset_denom || routeRequest.destAssetDenom,
        destAssetChainId: data.route?.dest_asset_chain_id || routeRequest.destAssetChainId,
        amountIn: data.route?.amount_in || routeRequest.amountIn,
        amountOut: data.route?.amount_out || '0',
        operations: data.route?.operations || [],
        requiredChainAddresses: data.route?.required_chain_addresses || [],
        chainIds: data.route?.chain_ids || [],
        doesSwap: data.route?.does_swap || false,
        estimatedAmountOut: data.route?.estimated_amount_out || '0',
        txsRequired: data.route?.txs_required || 1,
        usdAmountIn: data.route?.usd_amount_in,
        usdAmountOut: data.route?.usd_amount_out,
        swapPriceImpactPercent: data.route?.swap_price_impact_percent,
      },
      messages: {
        msgs: data.msgs || [],
        txs: data.txs || [],
        estimatedFees: data.estimated_fees,
      },
    };
  }

  /**
   * Submit a signed transaction
   */
  async submitTransaction(request: SkipGoSubmitRequest): Promise<SkipGoSubmitResponse> {
    const body = {
      tx: request.tx,
      chain_id: request.chainId,
    };

    const response = await fetch(`${getApiBase()}/v2/tx/submit`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to submit transaction: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      txHash: data.tx_hash,
      success: true,
    };
  }

  /**
   * Start tracking a transaction
   */
  async trackTransaction(txHash: string, chainId: string): Promise<void> {
    const body = {
      tx_hash: txHash,
      chain_id: chainId,
    };

    const response = await fetch(`${getApiBase()}/v2/tx/track`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to track transaction: ${response.statusText}`);
    }
  }

  /**
   * Get transaction status
   */
  async getTransactionStatus(txHash: string, chainId: string): Promise<SkipGoStatusResponse> {
    const params = new URLSearchParams({
      tx_hash: txHash,
      chain_id: chainId,
    });

    const response = await fetch(`${getApiBase()}/v2/tx/status?${params}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get transaction status: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Poll transaction status until complete or timeout
   */
  async waitForTransaction(
    txHash: string,
    chainId: string,
    options?: {
      timeoutMs?: number;
      pollIntervalMs?: number;
      onStatusUpdate?: (status: SkipGoStatusResponse) => void;
    }
  ): Promise<SkipGoStatusResponse> {
    const timeoutMs = options?.timeoutMs || 300000; // 5 minutes default
    const pollIntervalMs = options?.pollIntervalMs || 3000; // 3 seconds default
    const startTime = Date.now();

    // Start tracking
    await this.trackTransaction(txHash, chainId);

    while (Date.now() - startTime < timeoutMs) {
      const status = await this.getTransactionStatus(txHash, chainId);

      if (options?.onStatusUpdate) {
        options.onStatusUpdate(status);
      }

      // Check if transaction is complete
      if (
        status.state === 'STATE_COMPLETED_SUCCESS' ||
        status.state === 'STATE_COMPLETED_ERROR' ||
        status.state === 'STATE_ABANDONED'
      ) {
        return status;
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    throw new Error('Transaction tracking timed out');
  }
}

// Export singleton instance (can be configured with API key later)
export const skipGoApi = new SkipGoApiService();

// Export class for custom instances
export { SkipGoApiService };
